import React, { useEffect, useState, useCallback } from "react";
import API_BASE from "../config";

const SETTINGS_URL = `${API_BASE}/api/super-admin/settings`;

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: "Bearer " + (localStorage.getItem("idToken") || ""),
  };
}

function fmtDate(v) {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

const styles = {
  tabBtn: (active) => ({
    padding: "8px 16px",
    border: "none",
    borderBottom: active ? "3px solid #2563eb" : "3px solid transparent",
    background: "none",
    fontWeight: active ? 700 : 500,
    color: active ? "#1e3a8a" : "#475569",
    cursor: "pointer",
  }),
  catHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 14px",
    background: "#eef2ff",
    border: "1px solid #c7d2fe",
    borderRadius: 8,
    cursor: "pointer",
    marginTop: 10,
    fontWeight: 600,
    color: "#1e293b",
  },
  th: { textAlign: "left", padding: "8px", borderBottom: "2px solid #e2e8f0", fontSize: 13, color: "#475569" },
  td: { padding: "8px", borderBottom: "1px solid #eef2f7", verticalAlign: "top", fontSize: 13 },
  mono: { fontFamily: "monospace", wordBreak: "break-all" },
  badge: { fontSize: 11, background: "#fef3c7", color: "#92400e", borderRadius: 6, padding: "2px 6px", marginLeft: 6 },
  btn: { padding: "5px 10px", border: "1px solid #cbd5e1", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 12 },
  btnPrimary: { padding: "5px 10px", border: "none", borderRadius: 6, background: "#2563eb", color: "#fff", cursor: "pointer", fontSize: 12 },
  input: { width: "100%", padding: "6px", border: "1px solid #cbd5e1", borderRadius: 6, fontFamily: "monospace", fontSize: 13, boxSizing: "border-box" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#fff", borderRadius: 10, padding: 20, width: "80%", maxWidth: 900, maxHeight: "80vh", overflow: "auto" },
};

export default function Settings() {
  const [tab, setTab] = useState("settings");
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});
  const [editingKey, setEditingKey] = useState(null);
  const [draftValue, setDraftValue] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState("");
  const [historyKey, setHistoryKey] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const emptyNew = { setting_key: "", setting_value: "", category: "", value_type: "string", requires_restart: false, description: "", reason: "" };
  const [showAdd, setShowAdd] = useState(false);
  const [newS, setNewS] = useState(emptyNew);
  const [addBanner, setAddBanner] = useState("");
  const [deleteKey, setDeleteKey] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteBanner, setDeleteBanner] = useState("");

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(SETTINGS_URL, { headers: authHeaders(), credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSettings(data.settings || []);
    } catch (e) {
      setError("Failed to load settings: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const res = await fetch(`${SETTINGS_URL}/audit?limit=200`, { headers: authHeaders(), credentials: "include" });
      const data = await res.json();
      setAuditRows(data.audit || []);
    } catch {
      setAuditRows([]);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === "audit") loadAudit();
  }, [tab, loadAudit]);

  async function createSetting() {
    if (!newS.setting_key.trim() || !newS.reason.trim()) {
      setAddBanner("Key and reason are required.");
      return;
    }
    try {
      const res = await fetch(SETTINGS_URL, {
        method: "POST",
        headers: authHeaders(),
        credentials: "include",
        body: JSON.stringify(newS),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setShowAdd(false);
      setNewS(emptyNew);
      setBanner(`Added setting ${data.setting_key}.`);
      await loadSettings();
    } catch (e) {
      setAddBanner("Create failed: " + e.message);
    }
  }

  async function deleteSetting() {
    if (!deleteReason.trim()) { setDeleteBanner("A reason is required."); return; }
    try {
      const res = await fetch(`${SETTINGS_URL}/${encodeURIComponent(deleteKey)}`, {
        method: "DELETE",
        headers: authHeaders(),
        credentials: "include",
        body: JSON.stringify({ reason: deleteReason.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      const k = deleteKey;
      setDeleteKey(null);
      setDeleteReason("");
      setBanner(`Deleted setting ${k}. Running services fully drop it after a restart.`);
      await loadSettings();
    } catch (e) {
      setDeleteBanner("Delete failed: " + e.message);
    }
  }

  const addLbl = { display: "block", fontSize: 12, color: "#475569", margin: "8px 0 2px" };

  const groups = settings.reduce((acc, s) => {
    (acc[s.category] = acc[s.category] || []).push(s);
    return acc;
  }, {});
  const categories = Object.keys(groups).sort();

  function toggleCat(cat) {
    setExpanded((e) => ({ ...e, [cat]: !e[cat] }));
  }

  function startEdit(s) {
    setEditingKey(s.setting_key);
    setDraftValue(s.setting_value == null ? "" : s.setting_value);
    setReason("");
    setBanner("");
  }
  function cancelEdit() {
    setEditingKey(null);
    setDraftValue("");
    setReason("");
  }

  async function saveEdit(s) {
    if (!reason.trim()) {
      setBanner("A reason for the change is required.");
      return;
    }
    setSaving(true);
    setBanner("");
    try {
      const res = await fetch(`${SETTINGS_URL}/${encodeURIComponent(s.setting_key)}`, {
        method: "PUT",
        headers: authHeaders(),
        credentials: "include",
        body: JSON.stringify({ value: draftValue, reason: reason.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setBanner(
        `Saved ${s.setting_key}. ` +
          (data.requires_restart ? "⚠️ Applies after a service restart." : "Live within ~60s.")
      );
      cancelEdit();
      await loadSettings();
    } catch (e) {
      setBanner("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function openHistory(key) {
    setHistoryKey(key);
    setHistoryRows([]);
    try {
      const res = await fetch(`${SETTINGS_URL}/${encodeURIComponent(key)}/audit`, {
        headers: authHeaders(),
        credentials: "include",
      });
      const data = await res.json();
      setHistoryRows(data.audit || []);
    } catch {
      setHistoryRows([]);
    }
  }

  return (
    <div>
      <h1>Settings</h1>
      <p style={{ color: "#64748b" }}>
        Portal-managed application configuration. Sensitive secrets (API keys, DB credentials) are
        intentionally not shown here — they remain in server configuration.
      </p>

      <div style={{ borderBottom: "1px solid #e2e8f0", marginBottom: 16 }}>
        <button style={styles.tabBtn(tab === "settings")} onClick={() => setTab("settings")}>
          Settings
        </button>
        <button style={styles.tabBtn(tab === "audit")} onClick={() => setTab("audit")}>
          Audit Log
        </button>
      </div>

      {banner && (
        <div style={{ padding: 10, marginBottom: 12, background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 8 }}>
          {banner}
        </div>
      )}

      {tab === "settings" && (
        <>
          <div style={{ marginBottom: 10 }}>
            <button style={styles.btnPrimary} onClick={() => { setNewS(emptyNew); setAddBanner(""); setShowAdd(true); }}>
              ➕ Add setting
            </button>
          </div>
          {loading && <p>Loading settings…</p>}
          {error && <p style={{ color: "#b91c1c" }}>{error}</p>}
          {!loading &&
            !error &&
            categories.map((cat) => (
              <div key={cat}>
                <div style={styles.catHeader} onClick={() => toggleCat(cat)}>
                  <span>
                    {expanded[cat] ? "▼" : "▶"} {cat}
                  </span>
                  <span style={{ color: "#6366f1", fontWeight: 500 }}>{groups[cat].length}</span>
                </div>
                {expanded[cat] && (
                  <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 8 }}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Key</th>
                        <th style={styles.th}>Value</th>
                        <th style={{ ...styles.th, width: 120 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {groups[cat].map((s) => {
                        const isEditing = editingKey === s.setting_key;
                        return (
                          <tr key={s.setting_key}>
                            <td style={styles.td}>
                              <div style={{ fontWeight: 600 }}>
                                {s.setting_key}
                                {s.requires_restart === "Y" && (
                                  <span style={styles.badge}>restart required</span>
                                )}
                              </div>
                              {s.description && (
                                <div style={{ color: "#94a3b8", fontSize: 12 }}>{s.description}</div>
                              )}
                              {s.updated_by_email && (
                                <div style={{ color: "#cbd5e1", fontSize: 11 }}>
                                  last changed by {s.updated_by_email} · {fmtDate(s.updated_at)}
                                </div>
                              )}
                            </td>
                            <td style={styles.td}>
                              {isEditing ? (
                                <div>
                                  <input
                                    style={styles.input}
                                    value={draftValue}
                                    onChange={(e) => setDraftValue(e.target.value)}
                                  />
                                  <input
                                    style={{ ...styles.input, marginTop: 6, fontFamily: "inherit" }}
                                    placeholder="Reason for this change (required)"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                  />
                                </div>
                              ) : (
                                <span style={styles.mono}>{s.setting_value}</span>
                              )}
                            </td>
                            <td style={styles.td}>
                              {isEditing ? (
                                <div style={{ display: "flex", gap: 6 }}>
                                  <button style={styles.btnPrimary} disabled={saving} onClick={() => saveEdit(s)}>
                                    {saving ? "Saving…" : "Save"}
                                  </button>
                                  <button style={styles.btn} disabled={saving} onClick={cancelEdit}>
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <div style={{ display: "flex", gap: 6 }}>
                                  <button style={styles.btn} onClick={() => startEdit(s)}>
                                    Edit
                                  </button>
                                  <button style={styles.btn} onClick={() => openHistory(s.setting_key)}>
                                    History
                                  </button>
                                  <button
                                    style={{ ...styles.btn, color: "#b91c1c" }}
                                    onClick={() => { setDeleteKey(s.setting_key); setDeleteReason(""); setDeleteBanner(""); }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
        </>
      )}

      {tab === "audit" && (
        <>
          {auditLoading && <p>Loading audit log…</p>}
          {!auditLoading && (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={styles.th}>When</th>
                  <th style={styles.th}>Key</th>
                  <th style={styles.th}>Who</th>
                  <th style={styles.th}>Old → New</th>
                  <th style={styles.th}>Reason</th>
                  <th style={styles.th}>Where</th>
                </tr>
              </thead>
              <tbody>
                {auditRows.map((a) => (
                  <tr key={a.id}>
                    <td style={styles.td}>{fmtDate(a.changed_at)}</td>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{a.setting_key}</td>
                    <td style={styles.td}>{a.changed_by_email}</td>
                    <td style={{ ...styles.td, ...styles.mono }}>
                      <span style={{ color: "#b91c1c" }}>{a.old_value}</span>
                      {" → "}
                      <span style={{ color: "#15803d" }}>{a.new_value}</span>
                    </td>
                    <td style={styles.td}>{a.reason}</td>
                    <td style={{ ...styles.td, color: "#94a3b8" }}>{a.source_ip}</td>
                  </tr>
                ))}
                {auditRows.length === 0 && (
                  <tr>
                    <td style={styles.td} colSpan={6}>
                      No changes recorded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </>
      )}

      {showAdd && (
        <div style={styles.modalOverlay} onClick={() => setShowAdd(false)}>
          <div style={{ ...styles.modal, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Add setting</h3>
            {addBanner && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{addBanner}</div>}
            <label style={addLbl}>Key (letters, numbers, underscores)</label>
            <input style={styles.input} value={newS.setting_key} onChange={(e) => setNewS({ ...newS, setting_key: e.target.value })} />
            <label style={addLbl}>Value</label>
            <input style={styles.input} value={newS.setting_value} onChange={(e) => setNewS({ ...newS, setting_value: e.target.value })} />
            <label style={addLbl}>Category</label>
            <input style={styles.input} list="setting-cats" value={newS.category} onChange={(e) => setNewS({ ...newS, category: e.target.value })} />
            <datalist id="setting-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
            <label style={addLbl}>Type</label>
            <select style={styles.input} value={newS.value_type} onChange={(e) => setNewS({ ...newS, value_type: e.target.value })}>
              <option value="string">string</option>
              <option value="number">number</option>
              <option value="boolean">boolean</option>
              <option value="path">path</option>
              <option value="list">list</option>
            </select>
            <label style={{ ...addLbl, display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={newS.requires_restart} onChange={(e) => setNewS({ ...newS, requires_restart: e.target.checked })} />
              Requires service restart to take effect
            </label>
            <label style={addLbl}>Description</label>
            <input style={styles.input} value={newS.description} onChange={(e) => setNewS({ ...newS, description: e.target.value })} />
            <label style={addLbl}>Reason (required)</label>
            <input style={styles.input} value={newS.reason} onChange={(e) => setNewS({ ...newS, reason: e.target.value })} />
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button style={styles.btnPrimary} onClick={createSetting}>Create</button>
              <button style={styles.btn} onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {deleteKey && (
        <div style={styles.modalOverlay} onClick={() => setDeleteKey(null)}>
          <div style={{ ...styles.modal, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Delete setting</h3>
            <p style={{ fontSize: 14 }}>
              Delete <b>{deleteKey}</b>? This is recorded in the audit log. Running services keep the
              current value until their next restart.
            </p>
            {deleteBanner && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{deleteBanner}</div>}
            <input style={styles.input} placeholder="Reason (required)" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} />
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button style={{ ...styles.btnPrimary, background: "#b91c1c" }} onClick={deleteSetting}>Delete</button>
              <button style={styles.btn} onClick={() => setDeleteKey(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {historyKey && (
        <div style={styles.modalOverlay} onClick={() => setHistoryKey(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0 }}>History — {historyKey}</h3>
              <button style={styles.btn} onClick={() => setHistoryKey(null)}>
                Close
              </button>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }}>
              <thead>
                <tr>
                  <th style={styles.th}>When</th>
                  <th style={styles.th}>Who</th>
                  <th style={styles.th}>Old → New</th>
                  <th style={styles.th}>Reason</th>
                  <th style={styles.th}>Where</th>
                </tr>
              </thead>
              <tbody>
                {historyRows.map((a) => (
                  <tr key={a.id}>
                    <td style={styles.td}>{fmtDate(a.changed_at)}</td>
                    <td style={styles.td}>{a.changed_by_email}</td>
                    <td style={{ ...styles.td, ...styles.mono }}>
                      <span style={{ color: "#b91c1c" }}>{a.old_value}</span>
                      {" → "}
                      <span style={{ color: "#15803d" }}>{a.new_value}</span>
                    </td>
                    <td style={styles.td}>{a.reason}</td>
                    <td style={{ ...styles.td, color: "#94a3b8" }}>{a.source_ip}</td>
                  </tr>
                ))}
                {historyRows.length === 0 && (
                  <tr>
                    <td style={styles.td} colSpan={5}>
                      No changes recorded for this setting.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
